import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { calendarService, Calendar } from '@/features/calendar/services/calendar.service';
import { usersService } from '@/features/users/services/users.service';
import { usePermissions } from '@/common/hooks/usePermissions';
import { Checkbox } from '@/components/ui/checkbox';
import { RefreshCw, Trash2, Link as LinkIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

export const Route = createFileRoute('/_authenticated/calendar-config')({
  component: CalendarConfigPage,
});

export function CalendarConfigPage() {
  const { hasPermission } = usePermissions();
  const canConfig = hasPermission('calendar.config');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState<Calendar | null>(null);
  const [permissionsOpen, setPermissionsOpen] = useState(false);

  const { data: integrations = [], isLoading: integrationsLoading } = useQuery({
    queryKey: ['calendar-integrations'],
    queryFn: () => calendarService.getIntegrations(),
    enabled: canConfig,
  });

  const { data: calendars = [], isLoading: calendarsLoading } = useQuery({
    queryKey: ['calendars'],
    queryFn: () => calendarService.getCalendars(),
    enabled: canConfig,
  });

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canConfig,
  });

  const users = usersData || [];

  const connectMutation = useMutation({
    mutationFn: async () => {
      const { authUrl } = await calendarService.getAuthUrl();
      window.location.href = authUrl;
    },
    onSuccess: () => {
      setConnecting(true);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (integrationId: string) => calendarService.disconnectIntegration(integrationId),
    onSuccess: () => {
      toast({
        title: 'Integração desconectada',
        description: 'A integração foi desconectada com sucesso.',
      });
      queryClient.invalidateQueries({ queryKey: ['calendar-integrations'] });
      queryClient.invalidateQueries({ queryKey: ['calendars'] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => calendarService.triggerSync(),
    onSuccess: () => {
      toast({
        title: 'Sincronização iniciada',
        description: 'Os eventos serão atualizados em breve.',
      });
    },
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: ({ calendarId, blockedUserIds }: { calendarId: string; blockedUserIds: string[] }) =>
      calendarService.updateCalendarPermissions(calendarId, { blockedUserIds }),
    onSuccess: () => {
      toast({
        title: 'Permissões atualizadas',
        description: 'As permissões foram atualizadas com sucesso.',
      });
      setPermissionsOpen(false);
      queryClient.invalidateQueries({ queryKey: ['calendars'] });
      queryClient.invalidateQueries({ queryKey: ['calendar-permissions'] });
    },
  });

  const handleUpdatePermissions = (blockedUserIds: string[]) => {
    if (!selectedCalendar) return;
    updatePermissionsMutation.mutate({
      calendarId: selectedCalendar.id,
      blockedUserIds,
    });
  };

  if (!canConfig) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Você não tem permissão para acessar esta página.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Configuração de Calendários</h1>
        <Button onClick={() => connectMutation.mutate()} disabled={connecting}>
          <LinkIcon className="mr-2 h-4 w-4" />
          {connecting ? 'Conectando...' : 'Conectar Google Calendar'}
        </Button>
      </div>

      {integrationsLoading || calendarsLoading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Carregando...</p>
          </CardContent>
        </Card>
      ) : integrations.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center py-8">
              Nenhuma integração configurada. Clique em "Conectar Google Calendar" para começar.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {integrations.map((integration) => (
            <Card key={integration.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{integration.googleEmail}</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => syncMutation.mutate()}
                      disabled={syncMutation.isPending}
                    >
                      <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                      Sincronizar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => disconnectMutation.mutate(integration.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Desconectar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {calendars
                    .filter((cal) => cal.integrationId === integration.id)
                    .map((calendar) => (
                      <div
                        key={calendar.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div>
                          <div className="font-medium">{calendar.name}</div>
                          {calendar.description && (
                            <div className="text-sm text-muted-foreground">{calendar.description}</div>
                          )}
                          {calendar.syncedAt && (
                            <div className="text-xs text-muted-foreground mt-1">
                              Última sincronização: {new Date(calendar.syncedAt).toLocaleString('pt-BR')}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedCalendar(calendar);
                            setPermissionsOpen(true);
                          }}
                        >
                          Gerenciar Permissões
                        </Button>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PermissionsDialog
        open={permissionsOpen}
        onOpenChange={setPermissionsOpen}
        calendar={selectedCalendar}
        users={users}
        onSave={handleUpdatePermissions}
        isLoading={updatePermissionsMutation.isPending}
      />
    </div>
  );
}

interface PermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  calendar: Calendar | null;
  users: Array<{ id: string; email: string }>;
  onSave: (blockedUserIds: string[]) => void;
  isLoading: boolean;
}

export function PermissionsDialog({ open, onOpenChange, calendar, users, onSave, isLoading }: PermissionsDialogProps) {
  if (!calendar) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" busy={isLoading}>
        <DialogHeader>
          <DialogTitle>Gerenciar Permissões - {calendar.name}</DialogTitle>
          <DialogDescription>
            Por padrão, todos os usuários têm acesso. Desmarque os usuários que não devem ter acesso.
          </DialogDescription>
        </DialogHeader>
        {/* key por calendário: trocar de calendário nunca herda a seleção do anterior */}
        <PermissionsForm
          key={calendar.id}
          calendarId={calendar.id}
          users={users}
          onSave={onSave}
          onCancel={() => onOpenChange(false)}
          isLoading={isLoading}
        />
      </DialogContent>
    </Dialog>
  );
}

function PermissionsForm({
  calendarId,
  users,
  onSave,
  onCancel,
  isLoading,
}: {
  calendarId: string;
  users: Array<{ id: string; email: string }>;
  onSave: (blockedUserIds: string[]) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  // Carrega os bloqueios atuais: sem isso, salvar apagava todos os bloqueios existentes.
  const { data, isLoading: loadingCurrent, isError } = useQuery({
    queryKey: ['calendar-permissions', calendarId],
    queryFn: () => calendarService.getCalendarPermissions(calendarId),
  });
  const [edited, setEdited] = useState<Set<string> | null>(null);
  const blockedUserIds = edited ?? new Set(data?.blockedUserIds ?? []);

  const handleToggleUser = (userId: string) => {
    const newBlocked = new Set(blockedUserIds);
    if (newBlocked.has(userId)) {
      newBlocked.delete(userId);
    } else {
      newBlocked.add(userId);
    }
    setEdited(newBlocked);
  };

  return (
    <>
      <div className="space-y-2 py-4">
        {loadingCurrent ? (
          <p className="text-sm text-muted-foreground">Carregando permissões atuais…</p>
        ) : isError ? (
          <p className="text-sm text-destructive">
            Não foi possível carregar as permissões atuais. Feche e tente de novo para não sobrescrever os bloqueios.
          </p>
        ) : (
          users.map((user) => (
            <div key={user.id} className="flex items-center space-x-2">
              <Checkbox
                id={`perm-${user.id}`}
                checked={!blockedUserIds.has(user.id)}
                onCheckedChange={() => handleToggleUser(user.id)}
              />
              <label htmlFor={`perm-${user.id}`} className="text-sm font-medium leading-none cursor-pointer">
                {user.email}
              </label>
            </div>
          ))
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={() => onSave(Array.from(blockedUserIds))}
          disabled={loadingCurrent || isError}
          loading={isLoading}
        >
          Salvar permissões
        </Button>
      </DialogFooter>
    </>
  );
}
